import { isSameWeek, parseISO } from 'date-fns'
import { ArrowRightIcon, CheckIcon, SparklesIcon } from 'lucide-react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { currentWeekStart } from '../data/homework-data'
import {
  EXTRA_CREDIT_SUGGESTIONS,
  type ExtraCreditSuggestion,
} from '../data/mastery-data'
import { TRACK_IDENTITY } from '../data/task-config'
import { useHomework } from '../providers/homework-provider'

const TRACK_LABELS = {
  coding: 'Coding',
  'system-design': 'System design',
  behavioral: 'Behavioral',
} as const

/** Ties each suggestion to the concrete course lesson where you'd start */
const COURSE_JUMP: Record<
  string,
  { href: string; courseTitle: string; lessonTitle: string }
> = {
  'linked-lists': {
    href: '/courses/linked-structures?lesson=ls-2',
    courseTitle: 'Linked Lists & Stacks',
    lessonTitle: 'Reversal, step by step',
  },
  caching: {
    href: '/courses/sd-building-blocks?lesson=bb-1',
    courseTitle: 'Caching, Queues & Load Balancing',
    lessonTitle: 'Caching strategies compared',
  },
  'fav-project': {
    href: '/courses/story-craft?lesson=sc-3',
    courseTitle: 'Crafting Your Stories',
    lessonTitle: 'Quantifying your impact',
  },
}

function SuggestionRow({ suggestion }: { suggestion: ExtraCreditSuggestion }) {
  const identity = TRACK_IDENTITY[suggestion.track]
  const TrackIcon = identity.icon
  const done = suggestion.subtopics.filter((st) => st.mastered).length
  const total = suggestion.subtopics.length
  const pct = Math.round((done / total) * 100)
  const started = done > 0
  const jump = COURSE_JUMP[suggestion.id]

  return (
    <div className="grid items-center gap-3 md:grid-cols-[1fr_1.4fr_1fr] md:gap-6">
      {/* Because you know */}
      <div className="flex flex-wrap items-center justify-start gap-2 md:justify-end">
        {suggestion.becauseYouKnow.map((name) => (
          <span
            key={name}
            className="flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2.5 py-1 text-xs font-medium text-foreground/80"
          >
            <CheckIcon className="size-3 text-primary" aria-hidden="true" />
            {name}
          </span>
        ))}
      </div>

      {/* You can start now */}
      <div
        className={cn(
          'flex flex-col gap-2 rounded-lg border-2 p-3.5',
          suggestion.track === 'coding' && 'border-task-leetcode/40',
          suggestion.track === 'system-design' && 'border-task-design/40',
          suggestion.track === 'behavioral' && 'border-task-article/40',
        )}
      >
        <span
          className={cn(
            'flex items-center gap-1.5 self-start rounded-full px-2 py-0.5 text-xs font-medium',
            identity.chip,
          )}
        >
          <TrackIcon className="size-3.5" aria-hidden="true" />
          {TRACK_LABELS[suggestion.track]}
        </span>
        <span className="text-sm font-semibold">{suggestion.name}</span>
        <span className="text-xs text-muted-foreground">
          {started
            ? `Continue — ${pct}% · ${total - done} subtopic${total - done === 1 ? '' : 's'} left`
            : `Start fresh — ${total} subtopics`}
        </span>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn('h-full rounded-full transition-all', identity.bar)}
            style={{ width: `${pct}%` }}
          />
        </div>
        {jump && (
          <Button
            variant="outline"
            size="sm"
            asChild
            className="mt-1 h-auto justify-start self-start px-2.5 py-1.5 text-xs"
          >
            <Link href={jump.href}>
              <span className="flex flex-col items-start text-left">
                <span className="font-medium">Jump to course</span>
                <span className="font-normal text-muted-foreground">
                  {jump.courseTitle} · {jump.lessonTitle}
                </span>
              </span>
              <ArrowRightIcon />
            </Link>
          </Button>
        )}
      </div>

      {/* Which unlocks later */}
      <span className="text-xs text-muted-foreground italic">
        {suggestion.unlocksHint}
      </span>
    </div>
  )
}

export function ExtraCredit() {
  const { tasks, isCompleted } = useHomework()

  // Only appears once every task in the current sprint week is complete
  const weekStart = currentWeekStart()
  const thisWeeksTasks = tasks.filter((t) =>
    isSameWeek(parseISO(t.date), weekStart, { weekStartsOn: 1 }),
  )
  const allDone =
    thisWeeksTasks.length > 0 && thisWeeksTasks.every((t) => isCompleted(t))

  if (!allDone) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SparklesIcon className="size-4 text-primary" aria-hidden="true" />
          Extra Credit
        </CardTitle>
        <CardDescription>
          {
            "You're done with this week's homework — nice. Here are a few things your progress has opened up, if you want to get ahead."
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="hidden grid-cols-[1fr_1.4fr_1fr] gap-6 md:grid">
          <span className="text-right text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            Because you know
          </span>
          <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            You can start now
          </span>
          <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            Which unlocks
          </span>
        </div>

        <div className="flex flex-col gap-4">
          {EXTRA_CREDIT_SUGGESTIONS.map((suggestion) => (
            <SuggestionRow key={suggestion.id} suggestion={suggestion} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
